import styled, { css } from 'styled-components';

import { Copy, IconButton, TextRadio } from '@/components/elements';
import {
  blinkInputOpacityToPreventScrollingWhenFocused,
  focusWithinStyleDark,
  laptop,
} from '@/components/utils';

interface IStyledSearchInputProps {
  $error?: boolean;
}

export const StyledContainer = styled.div`
  position: relative;
`;

export const StyledSearchInputWrapper = styled.div`
  position: relative;
  width: 100%;

  ${focusWithinStyleDark};

  &:focus-within {
    &::after {
      top: -1px;
      right: -1px;
      bottom: -1px;
      left: -3px;
      border-radius: var(--border-radius-s);
    }
  }
`;

export const StyledSearchInput = styled.input`
  width: 100%;
  padding: var(--distance-dynamic-xxs) var(--distance-static-5xl)
    var(--distance-dynamic-xxs) var(--distance-static-s);
  background-color: var(--color-surface-light);
  caret-color: var(--color-searchinput-caret);
  color: var(--color-text-highlight);
  font-size: var(--font-size-dynamic-xs);
  line-height: var(--line-height-static-xl);
  border: none;
  appearance: textfield;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: var(--color-searchinput-placeholder);
  }

  &::-webkit-search-cancel-button {
    display: none;
  }

  &:focus {
    outline: none;
    // Prevent scrolling when focused
    animation: ${blinkInputOpacityToPreventScrollingWhenFocused} 0.01s;
  }
`;

export const StyledForm = styled.form<IStyledSearchInputProps>`
  display: flex;
  align-items: center;
  background-color: var(--color-input-text-enabled);
  position: relative;
  border-radius: var(--border-radius-s);
  border: 1px solid var(--color-input-border-idle);

  @media (hover: hover) {
    &:hover {
      border-color: var(
        ${({ $error }) =>
          $error ? '--color-input-border-error' : '--color-input-border-hover'}
      );
      box-shadow: var(--shadow-m);
    }
  }

  &:focus {
    outline: 0;
  }
`;

export const StyledIconButton = styled(IconButton)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: var(--distance-static-xs);
  cursor: pointer;

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: var(--color-cta-active);
    `};
`;

export const StyledIconCloseButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  right: var(--distance-dynamic-xl);
  transform: translateY(-50%);
  cursor: pointer;
  z-index: var(--zindex-m);
`;

export const StyledSuggestions = styled.ul`
  position: absolute;
  top: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: var(--color-light);
  border-radius: var(--border-radius-s);
  box-shadow: var(--shadow-m);
  list-style: none;
  z-index: var(--zindex-m);
  max-height: 350px;
  overflow-y: auto;
`;

export const StyledSuggestionItem = styled.li<{ $isActive: boolean }>`
  display: flex;
  padding: var(--distance-static-xs) var(--distance-static-s);
  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: var(--color-searchinput-result-hover);
    `};

  @media screen and (${laptop}) {
    padding: var(--distance-static-xs) var(--distance-static-xxl);
  }
`;

export const StyledSuggestionIcon = styled.div`
  display: flex;
  margin-right: var(--distance-static-s);
`;

export const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const StyledType = styled(Copy)`
  color: var(--color-text-dark-subdued);
  text-transform: capitalize;
`;

export const StyledTextRadio = styled(TextRadio)`
  margin-bottom: var(--distance-static-m);
`;
